import { useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Switch,
  Tag,
  TagLabel,
  Text,
  VStack,
  Wrap,
  useDisclosure,
  useToast,
  useColorModeValue,
  Badge,
} from '@chakra-ui/react';
import { EditIcon } from '@chakra-ui/icons';
import {
  getAuth,
  verifyBeforeUpdateEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { useStorage } from '../../hooks/useStorage';
import { callDeleteAccount, getPaymentErrorMessage } from '../../hooks/useStripe';
import { ReputationBadge } from '../common/ReputationBadge';
import { RESEARCH_TYPES } from '../../utils/formatters';
import { generateAnonymizedUsername } from '../../utils/anonymizer';

export default function Settings() {
  const { currentUser } = useAuth();
  const { userData } = useUser();
  const { uploadFile, uploading } = useStorage();
  const auth = getAuth();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const metaBg = useColorModeValue('gray.50', 'gray.700');
  const toast = useToast();
  const avatarInputRef = useRef(null);

  const rep = userData?.reputation;
  const specialties = userData?.specialties || [];

  // Email editing state
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailCurrentPassword, setEmailCurrentPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  // Password editing state
  const [editingPassword, setEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Username regeneration state
  const [usernameLoading, setUsernameLoading] = useState(false);

  // Delete account state
  const navigate = useNavigate();
  const deleteDialog = useDisclosure();
  const deleteCancelRef = useRef(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const cancelEmail = () => {
    setEditingEmail(false);
    setNewEmail('');
    setEmailCurrentPassword('');
  };

  const cancelPassword = () => {
    setEditingPassword(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleEmailSave = async () => {
    if (!newEmail || !emailCurrentPassword) return;
    setEmailLoading(true);
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, emailCurrentPassword);
      await reauthenticateWithCredential(user, credential);
      await verifyBeforeUpdateEmail(user, newEmail);
      toast({ title: 'Verification email sent', description: 'Check your new email inbox and click the verification link to complete the change.', status: 'success', duration: 6000, isClosable: true });
      cancelEmail();
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setEmailLoading(false);
    }
  };

  const handlePasswordSave = async () => {
    if (newPassword !== confirmPassword) {
      toast({ title: 'Passwords do not match', status: 'error', duration: 4000, isClosable: true });
      return;
    }
    if (newPassword.length < 6) {
      toast({ title: 'Password must be at least 6 characters', status: 'error', duration: 4000, isClosable: true });
      return;
    }
    setPasswordLoading(true);
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      toast({ title: 'Password updated', status: 'success', duration: 4000, isClosable: true });
      cancelPassword();
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 6000, isClosable: true });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Please select an image file', status: 'error', duration: 3000, isClosable: true });
      return;
    }
    try {
      const url = await uploadFile(file, `users/${currentUser.uid}/avatar`);
      await updateDoc(doc(db, 'users', currentUser.uid), { avatarUrl: url });
      toast({ title: 'Photo updated', status: 'success', duration: 3000, isClosable: true });
    } catch (err) {
      toast({ title: 'Error uploading photo', description: err.message, status: 'error', duration: 5000, isClosable: true });
    }
    e.target.value = '';
  };

  const toggleSpecialty = async (key) => {
    const current = userData?.specialties || [];
    const next = current.includes(key)
      ? current.filter((s) => s !== key)
      : [...current, key];
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { specialties: next });
    } catch (err) {
      toast({ title: 'Error saving specialty', status: 'error', duration: 3000, isClosable: true });
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    try {
      await callDeleteAccount();
      window.location.href = '/login';
    } catch (err) {
      toast({ title: 'Cannot delete account', description: getPaymentErrorMessage(err), status: 'error', duration: 6000, isClosable: true });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRegenerateUsername = async () => {
    setUsernameLoading(true);
    try {
      const newUsername = generateAnonymizedUsername();
      await updateDoc(doc(db, 'users', currentUser.uid), { anonymizedUsername: newUsername });
      toast({ title: 'Username updated', description: `Your new username is ${newUsername}`, status: 'success', duration: 4000, isClosable: true });
    } catch (err) {
      toast({ title: 'Error updating username', description: err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setUsernameLoading(false);
    }
  };

  return (
    <Container maxW="2xl" py={8}>
      <VStack spacing={8} align="stretch">
        <Box>
          <Heading size="lg">Account Settings</Heading>
          <Text color="gray.600" mt={1}>Manage your account and view your reputation</Text>
        </Box>

        <Box bg={cardBg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
          <VStack align="stretch" spacing={0} divider={<Divider />}>

            {/* Profile section header */}
            <Box px={5} py={3} bg={metaBg}>
              <Text fontWeight="semibold" fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                Profile
              </Text>
            </Box>

            {/* Avatar row */}
            <HStack px={5} py={4} justify="space-between" flexWrap="wrap" gap={2}>
              <Text fontSize="sm" color="gray.500">Photo</Text>
              <HStack spacing={3}>
                <Avatar
                  size="sm"
                  src={userData?.avatarUrl}
                  name={userData?.firstName || 'User'}
                  bg="orange.400"
                />
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => avatarInputRef.current?.click()}
                  isLoading={uploading}
                >
                  Change Photo
                </Button>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleAvatarChange}
                />
              </HStack>
            </HStack>

            {/* Username row */}
            <Box px={5} py={4}>
              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                <Text fontSize="sm" color="gray.500">Username</Text>
                <HStack spacing={2}>
                  <Text fontSize="sm" fontWeight="medium">{userData?.anonymizedUsername || '—'}</Text>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={handleRegenerateUsername}
                    isLoading={usernameLoading}
                  >
                    Generate New
                  </Button>
                </HStack>
              </HStack>
              <Text fontSize="xs" color="gray.400" mt={2}>
                We generate pseudonymous usernames for collectors, so that your collection stays private.
              </Text>
            </Box>

            {/* Email row */}
            <Box px={5} py={4}>
              {!editingEmail ? (
                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                  <Text fontSize="sm" color="gray.500">Email</Text>
                  <HStack spacing={1} minW={0}>
                    <Text fontSize="sm" noOfLines={1}>{auth.currentUser?.email || '—'}</Text>
                    <IconButton
                      icon={<EditIcon />}
                      size="xs"
                      variant="ghost"
                      aria-label="Edit email"
                      onClick={() => setEditingEmail(true)}
                    />
                  </HStack>
                </HStack>
              ) : (
                <VStack spacing={3} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" fontWeight="medium">Change Email</Text>
                    <Button size="xs" variant="ghost" onClick={cancelEmail}>Cancel</Button>
                  </HStack>
                  <FormControl>
                    <FormLabel fontSize="sm">Current password</FormLabel>
                    <Input
                      type="password"
                      size="sm"
                      value={emailCurrentPassword}
                      onChange={(e) => setEmailCurrentPassword(e.target.value)}
                      placeholder="Confirm with your current password"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">New email address</FormLabel>
                    <Input
                      type="email"
                      size="sm"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="new@email.com"
                    />
                  </FormControl>
                  <Button
                    colorScheme="orange"
                    size="sm"
                    alignSelf="flex-start"
                    isLoading={emailLoading}
                    isDisabled={!newEmail || !emailCurrentPassword}
                    onClick={handleEmailSave}
                  >
                    Save
                  </Button>
                </VStack>
              )}
            </Box>

            {/* Password row */}
            <Box px={5} py={4}>
              {!editingPassword ? (
                <HStack justify="space-between" flexWrap="wrap" gap={2}>
                  <Text fontSize="sm" color="gray.500">Password</Text>
                  <HStack spacing={1}>
                    <Text fontSize="sm" letterSpacing="widest" color="gray.400">••••••••</Text>
                    <IconButton
                      icon={<EditIcon />}
                      size="xs"
                      variant="ghost"
                      aria-label="Edit password"
                      onClick={() => setEditingPassword(true)}
                    />
                  </HStack>
                </HStack>
              ) : (
                <VStack spacing={3} align="stretch">
                  <HStack justify="space-between">
                    <Text fontSize="sm" fontWeight="medium">Change Password</Text>
                    <Button size="xs" variant="ghost" onClick={cancelPassword}>Cancel</Button>
                  </HStack>
                  <FormControl>
                    <FormLabel fontSize="sm">Current password</FormLabel>
                    <Input
                      type="password"
                      size="sm"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">New password</FormLabel>
                    <Input
                      type="password"
                      size="sm"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 6 characters"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Confirm new password</FormLabel>
                    <Input
                      type="password"
                      size="sm"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handlePasswordSave(); }}
                    />
                  </FormControl>
                  <Button
                    colorScheme="orange"
                    size="sm"
                    alignSelf="flex-start"
                    isLoading={passwordLoading}
                    isDisabled={!currentPassword || !newPassword || !confirmPassword}
                    onClick={handlePasswordSave}
                  >
                    Save
                  </Button>
                </VStack>
              )}
            </Box>

            {/* Research Specialties */}
            <Box px={5} py={3} bg={metaBg}>
              <Text fontWeight="semibold" fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                Research Specialties
              </Text>
            </Box>
            <Box px={5} py={4}>
              <Text fontSize="sm" color="gray.500" mb={3}>Select the research areas you specialise in.</Text>
              <Wrap spacing={2}>
                {Object.entries(RESEARCH_TYPES).map(([key, { label, color }]) => {
                  const active = specialties.includes(key);
                  return (
                    <Tag
                      key={key}
                      size="md"
                      cursor="pointer"
                      colorScheme={active ? color : 'gray'}
                      variant={active ? 'solid' : 'outline'}
                      onClick={() => toggleSpecialty(key)}
                      userSelect="none"
                    >
                      <TagLabel>{label}</TagLabel>
                    </Tag>
                  );
                })}
              </Wrap>
            </Box>

            {/* Notification Preferences */}
            <Box px={5} py={3} bg={metaBg}>
              <Text fontWeight="semibold" fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                Notifications
              </Text>
            </Box>
            <HStack px={5} py={4} justify="space-between" flexWrap="wrap" gap={2}>
              <Box flex="1" minW="200px">
                <Text fontSize="sm">Email notifications</Text>
                <Text fontSize="xs" color="gray.500">Receive email alerts for task and submission activity</Text>
              </Box>
              <Switch
                colorScheme="orange"
                isChecked={userData?.emailNotifications !== false}
                onChange={async (e) => {
                  try {
                    await updateDoc(doc(db, 'users', currentUser.uid), {
                      emailNotifications: e.target.checked,
                    });
                    toast({
                      title: e.target.checked ? 'Email notifications enabled' : 'Email notifications disabled',
                      status: 'success',
                      duration: 3000,
                      isClosable: true,
                    });
                  } catch (err) {
                    toast({ title: 'Error updating preference', status: 'error', duration: 3000, isClosable: true });
                  }
                }}
              />
            </HStack>

            {/* Privacy */}
            <Box px={5} py={3} bg={metaBg}>
              <Text fontWeight="semibold" fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                Privacy
              </Text>
            </Box>
            <Box px={5} py={4}>
              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                <Box flex="1" minW="200px">
                  <Text fontSize="sm">Fully Anonymous Mode</Text>
                  <Text fontSize="xs" color="gray.500">Hide your username, photo, specialties, and exact reputation from other users</Text>
                </Box>
                <Switch
                  colorScheme="orange"
                  isChecked={userData?.isFullyAnonymous === true}
                  onChange={async (e) => {
                    try {
                      await updateDoc(doc(db, 'users', currentUser.uid), {
                        isFullyAnonymous: e.target.checked,
                      });
                      toast({
                        title: e.target.checked ? 'Fully Anonymous Mode enabled' : 'Fully Anonymous Mode disabled',
                        description: e.target.checked
                          ? 'Other users will now see you as "Anonymous"'
                          : 'Your pseudonymous username is now visible to others',
                        status: 'success',
                        duration: 4000,
                        isClosable: true,
                      });
                    } catch (err) {
                      toast({ title: 'Error updating preference', status: 'error', duration: 3000, isClosable: true });
                    }
                  }}
                />
              </HStack>
              <Text fontSize="xs" color="gray.400" mt={3}>
                Accounts are pseudonymous by default, using a generated username and keeping your personal information private.
              </Text>
            </Box>

            {/* Reputation section */}
            {rep && (
              <>
                <Box px={5} py={3} bg={metaBg}>
                  <Text fontWeight="semibold" fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                    Reputation
                  </Text>
                </Box>
                <HStack px={5} py={4} justify="space-between" flexWrap="wrap" gap={2}>
                  <Text fontSize="sm" color="gray.500">Rating</Text>
                  <ReputationBadge reputation={rep} />
                </HStack>
                <HStack px={5} py={4} justify="space-between" flexWrap="wrap" gap={2}>
                  <Text fontSize="sm" color="gray.500">Completed tasks</Text>
                  <Badge colorScheme="green">{rep.completedBounties || 0}</Badge>
                </HStack>
              </>
            )}

            {/* Account Deletion */}
            <Box px={5} py={3} bg={metaBg}>
              <Text fontWeight="semibold" fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                Account Deletion
              </Text>
            </Box>
            <HStack px={5} py={4} justify="space-between" flexWrap="wrap" gap={2}>
              <Box flex="1" minW="200px">
                <Text fontSize="sm">Delete Account</Text>
                <Text fontSize="xs" color="gray.500">Permanently delete your account and all your data</Text>
              </Box>
              <Button colorScheme="red" variant="outline" size="sm" onClick={deleteDialog.onOpen} flexShrink={0}>
                Delete Account
              </Button>
            </HStack>

          </VStack>
        </Box>
      </VStack>

      {/* Delete Account Confirmation */}
      <AlertDialog isOpen={deleteDialog.isOpen} leastDestructiveRef={deleteCancelRef} onClose={deleteDialog.onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Delete Account</AlertDialogHeader>
            <AlertDialogBody>
              <Text mb={3}>Are you sure you want to delete your account? This action is <strong>permanent and irreversible</strong>.</Text>
              <Text fontSize="sm" color="gray.500">This will delete your profile, all your tasks, submissions, notifications, and any other associated data. Transaction history will be retained for record-keeping.</Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={deleteCancelRef} onClick={deleteDialog.onClose}>Cancel</Button>
              <Button colorScheme="red" onClick={handleDeleteAccount} isLoading={deleteLoading} ml={3}>
                Delete My Account
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
}
